import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import axios from '../../../utils/axios';
import SoftwareLicenceMultiSelect from './SoftwareLicenceMultiSelect';

const LifecyclePipelineView = ({ rawAssets = [], users = [], categories = [], softwareLicences = [], onAssetClick, onOpenAddAsset }) => {
  const [lifecycleAssets, setLifecycleAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sendingNotificationId, setSendingNotificationId] = useState(null);
  const [alertMessage, setAlertMessage] = useState(null);

  // Dynamic user & software licences states
  const [userList, setUserList] = useState(users);
  const [licenceList, setLicenceList] = useState(softwareLicences);

  useEffect(() => {
    if (users && users.length > 0) {
      setUserList(users);
    } else {
      axios.get('/admin/users')
        .then((res) => {
          const rows = Array.isArray(res.data) ? res.data : (Array.isArray(res.data?.data) ? res.data.data : []);
          setUserList(rows);
        })
        .catch((err) => console.error('Error fetching users for pipeline:', err));
    }
  }, [users]);

  useEffect(() => {
    if (softwareLicences && softwareLicences.length > 0) {
      setLicenceList(softwareLicences);
    } else {
      axios.get('/software-licences')
        .then((res) => {
          const body = res.data;
          const rows = Array.isArray(body) ? body : (Array.isArray(body?.data) ? body.data : []);
          setLicenceList(rows);
        })
        .catch((err) => console.error('Error fetching software licences for pipeline:', err));
    }
  }, [softwareLicences]);

  // Filter users who strictly have the role "Auditor"
  const auditorUsers = useMemo(() => {
    return userList.filter((u) => {
      const roleName = (typeof u.role === 'object' && u.role?.name)
        ? u.role.name
        : (typeof u.role === 'string' ? u.role : '');
      return roleName.trim().toLowerCase() === 'auditor';
    });
  }, [userList]);

  // Filters
  const [selectedStage, setSelectedStage] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [viewMode, setViewMode] = useState(() => {
    try {
      return localStorage.getItem('lifecycle_view_mode') || 'table';
    } catch {
      return 'table';
    }
  });

  const handleSetViewMode = (mode) => {
    setViewMode(mode);
    try {
      localStorage.setItem('lifecycle_view_mode', mode);
    } catch {
      // ignore
    }
  };

  // Modals & Drawer State
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [showJourneyDrawer, setShowJourneyDrawer] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [showRetireModal, setShowRetireModal] = useState(false);
  const [targetAsset, setTargetAsset] = useState(null);
  const [sendEmailNotification, setSendEmailNotification] = useState(true);

  // Forms
  const [assignForm, setAssignForm] = useState({ userId: '', department: 'Engineering', softwareLicenceIds: [], notes: 'Standard deployment' });
  const [transferForm, setTransferForm] = useState({ destinationLocation: 'Branch Office - Floor 2', destinationDepartment: 'Operations', newOwnerId: '', softwareLicenceIds: [], transferReason: 'Inter-branch project reassignment', inTransit: true });
  const [auditForm, setAuditForm] = useState({ condition: 'Optimal', auditResult: 'Verified', auditorName: '', notes: 'Physical barcode scanned. Hardware acceleration and battery health passed.' });
  const [retireForm, setRetireForm] = useState({ retirementReason: 'End of Useful Life (36m Cycle)', disposalMethod: 'Certified E-Waste Recycling', dataWipeCertified: true, salvageValue: '150', authorizedBy: 'Devinder Singh Dhillon (IT Director)', notes: 'Storage drive wiped per NIST 800-88 Clear Standard. Certificate archived.' });

  // Fetch Lifecycle Assets from Backend
  const fetchLifecycleData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get('/asset-lifecycle');
      if (res.data?.success && Array.isArray(res.data.data)) {
        setLifecycleAssets(res.data.data);
      }
    } catch (err) {
      console.error('Error fetching asset lifecycle:', err);
      // Fallback to rawAssets if provided
      if (rawAssets && rawAssets.length > 0) {
        const mapped = rawAssets.map((item) => ({
          ...item,
          stage: item.lifecycle_stage || (item.owner_id ? 'assigned' : 'registered'),
          stageStep: item.lifecycle_stage === 'retired' ? 5 : (item.lifecycle_stage === 'audit' ? 4 : (item.lifecycle_stage === 'transfer' ? 3 : (item.owner_id ? 2 : 1))),
          journey: item.lifecycle_journey || [
            { stage: 'Register', title: 'Asset Tagged', date: item.purchase_date || '2026-01-15', by: 'Procurement', details: `Barcode: ${item.barcode || item.asset_id}` }
          ]
        }));
        setLifecycleAssets(mapped);
      }
    } finally {
      setLoading(false);
    }
  }, [rawAssets]);

  useEffect(() => {
    fetchLifecycleData();
  }, [fetchLifecycleData]);

  // Sync when rawAssets updates
  useEffect(() => {
    if (rawAssets && rawAssets.length > 0 && lifecycleAssets.length === 0) {
      fetchLifecycleData();
    }
  }, [rawAssets, lifecycleAssets.length, fetchLifecycleData]);

  // Filtered Assets
  const filteredAssets = lifecycleAssets.filter((asset) => {
    const matchesStage = selectedStage === 'all' || asset.stage === selectedStage || asset.lifecycle_stage === selectedStage;
    const matchesCategory = categoryFilter === 'all' || (asset.category?.name || '').toLowerCase() === categoryFilter.toLowerCase();
    const matchesDepartment = departmentFilter === 'all' || (asset.department || '').toLowerCase() === departmentFilter.toLowerCase();
    const query = searchQuery.toLowerCase().trim();
    const matchesSearch = !query ||
      (asset.asset_id && asset.asset_id.toLowerCase().includes(query)) ||
      (asset.name && asset.name.toLowerCase().includes(query)) ||
      (asset.model && asset.model.toLowerCase().includes(query)) ||
      (asset.owner?.name && asset.owner.name.toLowerCase().includes(query)) ||
      (asset.serial_number && asset.serial_number.toLowerCase().includes(query));

    return matchesStage && matchesCategory && matchesDepartment && matchesSearch;
  });

  const stats = {
    total: lifecycleAssets.length,
    registered: lifecycleAssets.filter(a => (a.stage || a.lifecycle_stage) === 'registered').length,
    assigned: lifecycleAssets.filter(a => (a.stage || a.lifecycle_stage) === 'assigned').length,
    transfer: lifecycleAssets.filter(a => (a.stage || a.lifecycle_stage) === 'transfer').length,
    audit: lifecycleAssets.filter(a => (a.stage || a.lifecycle_stage) === 'audit').length,
    retired: lifecycleAssets.filter(a => (a.stage || a.lifecycle_stage) === 'retired').length
  };

  const stages = [
    {
      id: 'registered',
      step: 1,
      name: 'Register',
      label: '1. Register',
      icon: 'PackagePlus',
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10',
      borderColor: 'border-blue-500/20',
      count: stats.registered,
      subtitle: 'Onboarding & Barcode Tagging',
      description: 'Procured items received, linear barcode & QR encoded, placed into central inventory stock.'
    },
    {
      id: 'assigned',
      step: 2,
      name: 'Assign',
      label: '2. Assign',
      icon: 'UserCheck',
      color: 'text-indigo-500',
      bgColor: 'bg-indigo-500/10',
      borderColor: 'border-indigo-500/20',
      count: stats.assigned,
      subtitle: 'Custodian & Department Allocation',
      description: 'Deployed to verified employees, mapped to department cost-centers, email notification logged.'
    },
    {
      id: 'transfer',
      step: 3,
      name: 'Transfer',
      label: '3. Transfer',
      icon: 'ArrowRightLeft',
      color: 'text-amber-500',
      bgColor: 'bg-amber-500/10',
      borderColor: 'border-amber-500/20',
      count: stats.transfer,
      subtitle: 'Inter-Branch & Custody Movement',
      description: 'Dispatched between facilities, rooms or branches with transfer slip & in-transit tracking.'
    },
    {
      id: 'audit',
      step: 4,
      name: 'Audit',
      label: '4. Audit',
      icon: 'ClipboardCheck',
      color: 'text-purple-500',
      bgColor: 'bg-purple-500/10',
      borderColor: 'border-purple-500/20',
      count: stats.audit,
      subtitle: 'Physical Verification & Inspection',
      description: 'Regular barcode/RFID physical scans, condition rating, and discrepancy exception clearing.'
    },
    {
      id: 'retired',
      step: 5,
      name: 'Retire',
      label: '5. Retire',
      icon: 'Archive',
      color: 'text-rose-500',
      bgColor: 'bg-rose-500/10',
      borderColor: 'border-rose-500/20',
      count: stats.retired,
      subtitle: 'Decommission, Wipe & Disposal',
      description: 'NIST/DoD data wipe certification, salvage value recovery, and certified e-waste sign-off.'
    }
  ];

  // Action handlers
  const handleOpenAssign = (asset) => {
    setTargetAsset(asset);
    const currentLicenceIds = Array.isArray(asset.software_licences) && asset.software_licences.length > 0
      ? asset.software_licences.map(l => String(l.id))
      : (asset.software_licence_id ? [String(asset.software_licence_id)] : []);

    setAssignForm({
      userId: asset.owner_id || userList[0]?.id || '1',
      department: asset.department || 'Engineering',
      softwareLicenceIds: currentLicenceIds,
      notes: 'Assigned via Enterprise Lifecycle Management'
    });
    setSendEmailNotification(true);
    setShowAssignModal(true);
  };

  const submitAssign = async (e) => {
    e.preventDefault();
    if (!targetAsset) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`/assets/${targetAsset.id}/assign`, {
        userId: assignForm.userId,
        department: assignForm.department,
        softwareLicenceIds: assignForm.softwareLicenceIds || [],
        softwareLicenceId: assignForm.softwareLicenceIds?.[0] || null,
        notes: assignForm.notes,
        send_email: sendEmailNotification
      });

      const selectedUser = userList.find(u => String(u.id) === String(assignForm.userId));
      const userName = selectedUser?.name || res.data?.data?.owner?.name || 'employee';

      setAlertMessage({
        type: 'success',
        text: `Asset ${targetAsset.asset_id} deployed to ${userName}! ${res.data.email_sent ? 'Email notification sent to custodian.' : ''}`
      });

      await fetchLifecycleData();
      window.dispatchEvent(new CustomEvent('notificationUpdated'));
      setShowAssignModal(false);
    } catch (err) {
      console.error(err);
      setAlertMessage({
        type: 'error',
        text: err.response?.data?.message || 'Failed to assign asset.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenTransfer = (asset) => {
    setTargetAsset(asset);
    const currentLicenceIds = Array.isArray(asset.software_licences) && asset.software_licences.length > 0
      ? asset.software_licences.map(l => String(l.id))
      : (asset.software_licence_id ? [String(asset.software_licence_id)] : []);

    setTransferForm({
      destinationLocation: 'Branch Office - Floor 2',
      destinationDepartment: asset.department || 'Operations',
      newOwnerId: '',
      softwareLicenceIds: currentLicenceIds,
      transferReason: 'Inter-branch project reassignment',
      inTransit: true
    });
    setSendEmailNotification(true);
    setShowTransferModal(true);
  };

  const submitTransfer = async (e) => {
    e.preventDefault();
    if (!targetAsset) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`/assets/${targetAsset.id}/transfer`, {
        destinationLocation: transferForm.destinationLocation,
        destinationDepartment: transferForm.destinationDepartment,
        newOwnerId: transferForm.newOwnerId || null,
        softwareLicenceIds: transferForm.softwareLicenceIds || [],
        softwareLicenceId: transferForm.softwareLicenceIds?.[0] || null,
        transferReason: transferForm.transferReason,
        inTransit: transferForm.inTransit,
        send_email: sendEmailNotification
      });

      setAlertMessage({
        type: 'success',
        text: `Asset ${targetAsset.asset_id} transfer initiated to ${transferForm.destinationLocation}! ${res.data.email_sent ? 'Notice emailed.' : ''}`
      });

      await fetchLifecycleData();
      window.dispatchEvent(new CustomEvent('notificationUpdated'));
      setShowTransferModal(false);
    } catch (err) {
      console.error(err);
      setAlertMessage({
        type: 'error',
        text: err.response?.data?.message || 'Failed to transfer asset.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenAudit = (asset) => {
    setTargetAsset(asset);
    const defaultAuditor = auditorUsers.length > 0 ? auditorUsers[0].name : '';
    setAuditForm({
      condition: asset.condition || 'Optimal',
      auditResult: 'Verified',
      auditorName: defaultAuditor,
      notes: 'Physical barcode scanned. Hardware diagnostic verified.'
    });
    setShowAuditModal(true);
  };

  const submitAudit = async (e) => {
    e.preventDefault();
    if (!targetAsset) return;
    setSubmitting(true);
    try {
      await axios.post(`/assets/${targetAsset.id}/audit`, {
        condition: auditForm.condition,
        auditResult: auditForm.auditResult,
        auditorName: auditForm.auditorName,
        notes: auditForm.notes
      });

      setAlertMessage({
        type: 'success',
        text: `Audit recorded for ${targetAsset.asset_id}: ${auditForm.auditResult} (${auditForm.condition}).`
      });

      await fetchLifecycleData();
      window.dispatchEvent(new CustomEvent('notificationUpdated'));
      setShowAuditModal(false);
    } catch (err) {
      console.error(err);
      setAlertMessage({
        type: 'error',
        text: err.response?.data?.message || 'Failed to record audit verification.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenRetire = (asset) => {
    setTargetAsset(asset);
    setRetireForm({
      retirementReason: 'End of Useful Life (36m Cycle)',
      disposalMethod: 'Certified E-Waste Recycling',
      dataWipeCertified: true,
      salvageValue: asset.salvage_value || '150',
      authorizedBy: 'IT Director',
      notes: 'Storage drive wiped per NIST 800-88 Clear Standard.'
    });
    setSendEmailNotification(true);
    setShowRetireModal(true);
  };

  const submitRetire = async (e) => {
    e.preventDefault();
    if (!targetAsset) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`/assets/${targetAsset.id}/retire`, {
        retirementReason: retireForm.retirementReason,
        disposalMethod: retireForm.disposalMethod,
        salvageValue: retireForm.salvageValue,
        dataWipeCertified: retireForm.dataWipeCertified,
        authorizedBy: retireForm.authorizedBy,
        notes: retireForm.notes,
        send_email: sendEmailNotification
      });

      setAlertMessage({
        type: 'success',
        text: `Asset ${targetAsset.asset_id} permanently retired & data wipe certified! ${res.data.email_sent ? 'Certificate emailed.' : ''}`
      });

      await fetchLifecycleData();
      window.dispatchEvent(new CustomEvent('notificationUpdated'));
      setShowRetireModal(false);
    } catch (err) {
      console.error(err);
      setAlertMessage({
        type: 'error',
        text: err.response?.data?.message || 'Failed to decommission asset.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Send on-demand lifecycle notification email
  const handleSendNotification = async (asset) => {
    try {
      setSendingNotificationId(asset.id);
      const res = await axios.post(`/assets/${asset.id}/send-notification`, {
        email: asset.owner?.email,
        name: asset.owner?.name,
        message: `Status notification for equipment ${asset.name} (${asset.asset_id}) currently in stage ${(asset.stage || asset.lifecycle_stage)?.toUpperCase()}.`
      });

      setAlertMessage({
        type: 'success',
        text: `Lifecycle status email dispatched for ${asset.asset_id} to ${asset.owner?.email || 'custodian'}!`
      });
      window.dispatchEvent(new CustomEvent('notificationUpdated'));
    } catch (err) {
      console.error(err);
      setAlertMessage({
        type: 'error',
        text: 'Failed to send lifecycle notification email.'
      });
    } finally {
      setSendingNotificationId(null);
    }
  };

  const handleViewJourney = (asset) => {
    setSelectedAsset(asset);
    setShowJourneyDrawer(true);
  };

  const getStageBadge = (stage) => {
    const s = (stage || '').toLowerCase();
    switch (s) {
      case 'registered':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-500/10 text-blue-500 border border-blue-500/20">1. Register</span>;
      case 'assigned':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-500/10 text-indigo-500 border border-indigo-500/20">2. Assign</span>;
      case 'transfer':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-500/10 text-amber-500 border border-amber-500/20">3. Transfer</span>;
      case 'audit':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-500/10 text-purple-500 border border-purple-500/20">4. Audit</span>;
      case 'retired':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-rose-500/10 text-rose-500 border border-rose-500/20">5. Retired</span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-muted text-muted-foreground">Active</span>;
    }
  };

  const departmentsList = Array.from(new Set(lifecycleAssets.map(a => a.department).filter(Boolean)));
  const categoriesList = Array.from(new Set(lifecycleAssets.map(a => a.category?.name).filter(Boolean)));

  return (
    <div className="space-y-6">
      {/* Alert Banner */}
      {alertMessage && (
        <div className={`p-4 rounded-xl border flex items-center justify-between transition-all ${
          alertMessage.type === 'success' 
            ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600' 
            : 'bg-rose-500/10 border-rose-500/30 text-rose-600'
        }`}>
          <div className="flex items-center gap-3 text-sm font-medium">
            <Icon name={alertMessage.type === 'success' ? 'CheckCircle2' : 'AlertCircle'} size={18} />
            <span>{alertMessage.text}</span>
          </div>
          <button onClick={() => setAlertMessage(null)} className="p-1 hover:opacity-75">
            <Icon name="X" size={16} />
          </button>
        </div>
      )}

      {/* Header & Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-2 border-b border-border">
        <div>
          <h2 className="text-xl font-bold text-foreground">Asset Lifecycle Pipeline</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            5-Stage Complete Lifecycle: Register ➔ Assign ➔ Transfer ➔ Audit ➔ Retire with Notifications & Live Database Sync
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Dual View Switcher */}
          <div className="flex items-center rounded-lg border border-border p-0.5 bg-muted/40 shadow-xs">
            <button
              type="button"
              onClick={() => handleSetViewMode('table')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                viewMode === 'table'
                  ? 'bg-primary text-primary-foreground shadow-xs'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              title="Table View"
            >
              <Icon name="Table" size={14} />
              <span>Table</span>
            </button>
            <button
              type="button"
              onClick={() => handleSetViewMode('card')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                viewMode === 'card'
                  ? 'bg-primary text-primary-foreground shadow-xs'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              title="Card View"
            >
              <Icon name="LayoutGrid" size={14} />
              <span>Cards</span>
            </button>
          </div>

          <Button
            variant="outline"
            size="sm"
            iconName="RefreshCw"
            iconPosition="left"
            onClick={fetchLifecycleData}
            disabled={loading}
          >
            {loading ? 'Syncing...' : 'Sync Pipeline'}
          </Button>
          {onOpenAddAsset && (
            <Button
              variant="default"
              size="sm"
              iconName="Plus"
              iconPosition="left"
              onClick={onOpenAddAsset}
            >
              Register Asset
            </Button>
          )}
        </div>
      </div>

      {/* Stage Cards (5 Stages) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {stages.map((stage) => {
          const isSelected = selectedStage === stage.id;
          return (
            <div
              key={stage.id}
              onClick={() => setSelectedStage(isSelected ? 'all' : stage.id)}
              className={`p-4 rounded-xl border cursor-pointer transition-all ${
                isSelected
                  ? `border-primary shadow-md ${stage.bgColor}`
                  : `bg-card border-border hover:border-primary/40`
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm ${stage.bgColor} ${stage.color}`}>
                  <Icon name={stage.icon} size={16} />
                </span>
                <span className={`text-xl font-bold ${stage.color}`}>{stage.count}</span>
              </div>
              <div className="font-semibold text-sm text-foreground">{stage.label}</div>
              <p className="text-[11px] text-muted-foreground mt-1 line-clamp-2 leading-tight">
                {stage.subtitle}
              </p>
            </div>
          );
        })}
      </div>

      {/* Filter and Search Bar */}
      <div className="bg-card border border-border rounded-xl p-4 shadow-sm flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-[240px]">
          <div className="relative w-full max-w-sm">
            <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search tag, device name, model, serial..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-1.5 bg-background border border-border rounded-lg text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          {selectedStage !== 'all' && (
            <button
              onClick={() => setSelectedStage('all')}
              className="text-xs text-primary font-semibold hover:underline flex items-center gap-1"
            >
              <Icon name="X" size={12} />
              Clear Stage ({selectedStage})
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center rounded-lg border border-border p-0.5 bg-muted/40 shadow-xs">
            <button
              type="button"
              onClick={() => handleSetViewMode('table')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                viewMode === 'table'
                  ? 'bg-card text-foreground shadow-xs border border-border/60'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              title="Table View"
            >
              <Icon name="Table" size={14} />
              <span>Table</span>
            </button>
            <button
              type="button"
              onClick={() => handleSetViewMode('card')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                viewMode === 'card'
                  ? 'bg-card text-foreground shadow-xs border border-border/60'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              title="Card View"
            >
              <Icon name="LayoutGrid" size={14} />
              <span>Cards</span>
            </button>
          </div>

          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-2.5 py-1.5 bg-background border border-border rounded-lg text-xs text-foreground"
          >
            <option value="all">All Categories</option>
            {categoriesList.map(c => <option key={c} value={c}>{c}</option>)}
          </select>

          <select
            value={departmentFilter}
            onChange={(e) => setDepartmentFilter(e.target.value)}
            className="px-2.5 py-1.5 bg-background border border-border rounded-lg text-xs text-foreground"
          >
            <option value="all">All Departments</option>
            {departmentsList.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
      </div>

      {/* Assets Grid / Table */}
      {viewMode === 'card' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredAssets.length === 0 ? (
            <div className="col-span-full bg-card border border-border rounded-xl p-8 text-center text-muted-foreground text-xs">
              No equipment found matching the selected stage and filters.
            </div>
          ) : (
            filteredAssets.map((asset) => (
              <div
                key={asset.id}
                className="bg-card border border-border rounded-xl p-5 shadow-sm hover:shadow-md transition-all flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div>
                      <h4 className="font-bold text-sm text-foreground">{asset.name || `${asset.brand} ${asset.model}`}</h4>
                      <div className="text-[11px] text-muted-foreground font-mono mt-0.5">
                        {asset.asset_id} • SN: {asset.serial_number || 'N/A'}
                      </div>
                    </div>
                    {getStageBadge(asset.stage || asset.lifecycle_stage)}
                  </div>

                  <div className="space-y-2 py-3 border-y border-border text-xs">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground flex items-center gap-1.5">
                        <Icon name="User" size={13} /> Custodian:
                      </span>
                      <span className="font-medium text-foreground">{asset.owner?.name || 'Central IT Pool'}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground flex items-center gap-1.5">
                        <Icon name="Building" size={13} /> Dept:
                      </span>
                      <span className="font-medium text-foreground">{asset.department || 'Unassigned'}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground flex items-center gap-1.5">
                        <Icon name="MapPin" size={13} /> Location:
                      </span>
                      <span className="font-medium text-foreground text-right truncate max-w-[180px]" title={asset.location}>
                        {asset.location || 'HQ Stockroom'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground flex items-center gap-1.5">
                        <Icon name="ShieldCheck" size={13} /> Audit Status:
                      </span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold ${
                        (asset.audit_status || '').includes('Verified') ? 'bg-emerald-500/10 text-emerald-500' :
                        (asset.audit_status || '').includes('Transit') ? 'bg-amber-500/10 text-amber-500' :
                        (asset.audit_status || '').includes('Decommissioned') ? 'bg-rose-500/10 text-rose-500' :
                        'bg-purple-500/10 text-purple-500'
                      }`}>
                        {asset.audit_status || 'Verified'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="pt-3 flex items-center justify-between gap-1.5">
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleViewJourney(asset)}
                      title="View Milestone Journey"
                      className="p-1.5 rounded-lg border border-border hover:bg-muted text-muted-foreground hover:text-foreground"
                    >
                      <Icon name="Eye" size={14} />
                    </button>
                    <button
                      onClick={() => handleSendNotification(asset)}
                      title="Send Status Email Notification"
                      disabled={sendingNotificationId === asset.id}
                      className="p-1.5 rounded-lg border border-border hover:bg-blue-500/10 text-blue-500"
                    >
                      <Icon name={sendingNotificationId === asset.id ? "Loader2" : "Mail"} size={14} className={sendingNotificationId === asset.id ? "animate-spin" : ""} />
                    </button>
                  </div>

                  <div className="flex items-center gap-1">
                    {(asset.stage === 'registered' || asset.lifecycle_stage === 'registered') && (
                      <Button size="xs" variant="default" onClick={() => handleOpenAssign(asset)}>
                        2. Assign
                      </Button>
                    )}
                    {(asset.stage === 'assigned' || asset.lifecycle_stage === 'assigned') && (
                      <div className="flex gap-1">
                        <Button size="xs" variant="outline" onClick={() => handleOpenTransfer(asset)}>
                          Transfer
                        </Button>
                        <Button size="xs" variant="outline" onClick={() => handleOpenAudit(asset)}>
                          Audit
                        </Button>
                        <Button size="xs" variant="destructive" onClick={() => handleOpenRetire(asset)}>
                          Retire
                        </Button>
                      </div>
                    )}
                    {(asset.stage === 'transfer' || asset.lifecycle_stage === 'transfer') && (
                      <Button size="xs" variant="outline" onClick={() => handleOpenAudit(asset)}>
                        4. Audit Check
                      </Button>
                    )}
                    {(asset.stage === 'audit' || asset.lifecycle_stage === 'audit') && (
                      <Button size="xs" variant="destructive" onClick={() => handleOpenRetire(asset)}>
                        5. Retire Asset
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      ) : (
        <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-border bg-muted/20 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="font-bold text-sm text-foreground">Pipeline Assets</h3>
              <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-primary/10 text-primary">
                {filteredAssets.length} Equipment
              </span>
            </div>
            <span className="text-xs text-muted-foreground">
              Fetched live from enterprise assets database
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-muted/40 text-muted-foreground uppercase text-[10px] font-semibold tracking-wider border-b border-border">
                <tr>
                  <th className="py-3 px-4">Asset Identification</th>
                  <th className="py-3 px-4">Current Stage</th>
                  <th className="py-3 px-4">Custodian & Department</th>
                  <th className="py-3 px-4">Location / Facility</th>
                  <th className="py-3 px-4">Audit Status</th>
                  <th className="py-3 px-4 text-right">Actions & Transitions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredAssets.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-muted-foreground">
                      No equipment found matching the selected stage and filters.
                    </td>
                  </tr>
                ) : (
                  filteredAssets.map((asset) => (
                    <tr key={asset.id} className="hover:bg-muted/30 transition-colors">
                      <td className="py-3 px-4">
                        <div className="font-semibold text-foreground text-xs">{asset.name || `${asset.brand} ${asset.model}`}</div>
                        <div className="text-[11px] text-muted-foreground font-mono">
                          {asset.asset_id} • SN: {asset.serial_number || 'N/A'}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        {getStageBadge(asset.stage || asset.lifecycle_stage)}
                      </td>
                      <td className="py-3 px-4">
                        <div className="font-medium text-foreground">{asset.owner?.name || 'Central IT Pool'}</div>
                        <div className="text-[11px] text-muted-foreground">{asset.department || 'Unassigned'}</div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-foreground">{asset.location || 'HQ Stockroom'}</div>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold ${
                          (asset.audit_status || '').includes('Verified') ? 'bg-emerald-500/10 text-emerald-500' :
                          (asset.audit_status || '').includes('Transit') ? 'bg-amber-500/10 text-amber-500' :
                          (asset.audit_status || '').includes('Decommissioned') ? 'bg-rose-500/10 text-rose-500' :
                          'bg-purple-500/10 text-purple-500'
                        }`}>
                          {asset.audit_status || 'Verified'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => handleViewJourney(asset)}
                            title="View Milestone Journey"
                            className="p-1.5 rounded-lg border border-border hover:bg-muted text-muted-foreground hover:text-foreground"
                          >
                            <Icon name="Eye" size={14} />
                          </button>
                          <button
                            onClick={() => handleSendNotification(asset)}
                            title="Send Status Email Notification"
                            disabled={sendingNotificationId === asset.id}
                            className="p-1.5 rounded-lg border border-border hover:bg-blue-500/10 text-blue-500"
                          >
                            <Icon name={sendingNotificationId === asset.id ? "Loader2" : "Mail"} size={14} className={sendingNotificationId === asset.id ? "animate-spin" : ""} />
                          </button>

                          {/* Stage Specific Action Trigger */}
                          {(asset.stage === 'registered' || asset.lifecycle_stage === 'registered') && (
                            <Button size="xs" variant="default" onClick={() => handleOpenAssign(asset)}>
                              2. Assign
                            </Button>
                          )}
                          {(asset.stage === 'assigned' || asset.lifecycle_stage === 'assigned') && (
                            <div className="flex gap-1">
                              <Button size="xs" variant="outline" onClick={() => handleOpenTransfer(asset)}>
                                Transfer
                              </Button>
                              <Button size="xs" variant="outline" onClick={() => handleOpenAudit(asset)}>
                                Audit
                              </Button>
                              <Button size="xs" variant="destructive" onClick={() => handleOpenRetire(asset)}>
                                Retire
                              </Button>
                            </div>
                          )}
                          {(asset.stage === 'transfer' || asset.lifecycle_stage === 'transfer') && (
                            <Button size="xs" variant="outline" onClick={() => handleOpenAudit(asset)}>
                              4. Audit Check
                            </Button>
                          )}
                          {(asset.stage === 'audit' || asset.lifecycle_stage === 'audit') && (
                            <Button size="xs" variant="destructive" onClick={() => handleOpenRetire(asset)}>
                              5. Retire Asset
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Milestone Journey Drawer */}
      {showJourneyDrawer && selectedAsset && (
        <div className="fixed inset-0 z-50 overflow-hidden bg-black/60 flex justify-end">
          <div className="w-full max-w-lg bg-card h-full shadow-2xl flex flex-col border-l border-border animate-in slide-in-from-right duration-200">
            {/* Header */}
            <div className="p-5 border-b border-border flex items-center justify-between bg-muted/20">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-bold text-lg text-foreground">{selectedAsset.name}</h3>
                  {getStageBadge(selectedAsset.stage || selectedAsset.lifecycle_stage)}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5 font-mono">
                  Tag: {selectedAsset.asset_id} • SN: {selectedAsset.serial_number || 'N/A'}
                </p>
              </div>
              <button
                onClick={() => setShowJourneyDrawer(false)}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted"
              >
                <Icon name="X" size={18} />
              </button>
            </div>

            {/* Content Timeline */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              <div className="p-3 bg-muted/40 rounded-lg text-xs space-y-1">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Current Stage:</span>
                  <span className="font-semibold text-foreground capitalize">{selectedAsset.stage || selectedAsset.lifecycle_stage}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Custodian:</span>
                  <span className="font-semibold text-foreground">{selectedAsset.owner?.name || 'Central Stock'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Location:</span>
                  <span className="font-semibold text-foreground">{selectedAsset.location}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Audit Status:</span>
                  <span className="font-semibold text-foreground">{selectedAsset.audit_status || 'Verified'}</span>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-xs uppercase font-bold tracking-wider text-muted-foreground">
                    Lifecycle Audit Trail & Milestones
                  </h4>
                  <Button
                    size="xs"
                    variant="outline"
                    iconName="Mail"
                    iconPosition="left"
                    onClick={() => handleSendNotification(selectedAsset)}
                    disabled={sendingNotificationId === selectedAsset.id}
                  >
                    Email Notification
                  </Button>
                </div>
                <div className="relative pl-6 space-y-6 before:absolute before:left-2 before:top-2 before:bottom-2 before:w-0.5 before:bg-border">
                  {(selectedAsset.journey || selectedAsset.lifecycle_journey || []).map((event, idx) => (
                    <div key={idx} className="relative">
                      <div className="absolute -left-6 top-1 w-4 h-4 rounded-full bg-primary border-2 border-background flex items-center justify-center text-[8px] text-primary-foreground font-bold" />
                      <div className="bg-muted/30 border border-border rounded-lg p-3 text-xs">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-bold text-foreground">{event.title}</span>
                          <span className="text-[11px] text-muted-foreground font-mono">{event.date}</span>
                        </div>
                        <div className="text-muted-foreground text-[11px] mb-1.5">{event.details}</div>
                        <div className="text-[10px] text-primary font-medium flex items-center gap-1">
                          <Icon name="User" size={10} />
                          <span>Recorded by {event.by}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-border flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowJourneyDrawer(false)}
              >
                Close Drawer
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Modal (Stage 2) */}
      {showAssignModal && targetAsset && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-card w-full max-w-md rounded-xl border border-border shadow-2xl p-6">
            <h3 className="text-lg font-bold text-foreground mb-1">Stage 2: Assign Asset Custodian</h3>
            <p className="text-xs text-muted-foreground mb-4">
              Allocate {targetAsset.name} ({targetAsset.asset_id}) to an employee and dispatch provisioning email.
            </p>
            <form onSubmit={submitAssign} className="space-y-4 text-xs">
              <div>
                <label className="block font-semibold mb-1 text-foreground">Select Custodian / Employee</label>
                <select
                  value={assignForm.userId}
                  onChange={(e) => {
                    const selectedU = userList.find(u => String(u.id) === String(e.target.value));
                    setAssignForm({
                      ...assignForm,
                      userId: e.target.value,
                      department: selectedU?.department || assignForm.department
                    });
                  }}
                  className="w-full bg-background border border-border rounded-lg p-2 text-foreground"
                >
                  {userList.map(u => (
                    <option key={u.id} value={u.id}>{u.name} ({u.email || u.department || 'Employee'})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block font-semibold mb-1 text-foreground">Department Cost Center</label>
                <input
                  type="text"
                  value={assignForm.department}
                  onChange={(e) => setAssignForm({ ...assignForm, department: e.target.value })}
                  placeholder="Auto-filled from custodian"
                  className="w-full bg-background border border-border rounded-lg p-2 text-foreground"
                  required
                />
              </div>
              <div>
                <SoftwareLicenceMultiSelect
                  licences={licenceList}
                  selectedIds={assignForm.softwareLicenceIds || []}
                  onChange={(ids) => setAssignForm((prev) => ({ ...prev, softwareLicenceIds: ids }))}
                  label="Software & Licences"
                  helperText="(Optional - Select multiple enterprise software)"
                  placeholder="Select software licences to deploy..."
                />
              </div>
              <div>
                <label className="block font-semibold mb-1 text-foreground">Deployment Notes</label>
                <textarea
                  value={assignForm.notes}
                  onChange={(e) => setAssignForm({ ...assignForm, notes: e.target.value })}
                  rows={2}
                  className="w-full bg-background border border-border rounded-lg p-2 text-foreground"
                />
              </div>

              <div className="p-3 bg-muted/40 rounded-lg space-y-1">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="assignEmailCheck"
                    checked={sendEmailNotification}
                    onChange={(e) => setSendEmailNotification(e.target.checked)}
                    className="rounded border-border text-primary"
                  />
                  <label htmlFor="assignEmailCheck" className="text-foreground font-semibold">
                    Send Email Notification to Employee
                  </label>
                </div>
                <p className="text-[11px] text-muted-foreground pl-5">
                  Sends official deployment receipt with serial number, tag, and acceptance instructions.
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" size="sm" type="button" onClick={() => setShowAssignModal(false)}>
                  Cancel
                </Button>
                <Button variant="default" size="sm" type="submit" disabled={submitting}>
                  {submitting ? 'Assigning...' : 'Confirm Assignment'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Transfer Modal (Stage 3) */}
      {showTransferModal && targetAsset && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-card w-full max-w-md rounded-xl border border-border shadow-2xl p-6">
            <h3 className="text-lg font-bold text-foreground mb-1">Stage 3: Transfer Asset Location</h3>
            <p className="text-xs text-muted-foreground mb-4">
              Move {targetAsset.name} ({targetAsset.asset_id}) across physical locations or branches.
            </p>
            <form onSubmit={submitTransfer} className="space-y-4 text-xs">
              <div>
                <label className="block font-semibold mb-1 text-foreground">Destination Facility / Branch</label>
                <input
                  type="text"
                  value={transferForm.destinationLocation}
                  onChange={(e) => setTransferForm({ ...transferForm, destinationLocation: e.target.value })}
                  className="w-full bg-background border border-border rounded-lg p-2 text-foreground"
                  required
                />
              </div>
              <div>
                <label className="block font-semibold mb-1 text-foreground">Destination Department</label>
                <input
                  type="text"
                  value={transferForm.destinationDepartment}
                  onChange={(e) => setTransferForm({ ...transferForm, destinationDepartment: e.target.value })}
                  className="w-full bg-background border border-border rounded-lg p-2 text-foreground"
                />
              </div>
              <div>
                <label className="block font-semibold mb-1 text-foreground">Optional New Custodian</label>
                <select
                  value={transferForm.newOwnerId}
                  onChange={(e) => {
                    const selectedOwner = userList.find(u => String(u.id) === String(e.target.value));
                    setTransferForm({
                      ...transferForm,
                      newOwnerId: e.target.value,
                      destinationDepartment: selectedOwner?.department || transferForm.destinationDepartment
                    });
                  }}
                  className="w-full bg-background border border-border rounded-lg p-2 text-foreground"
                >
                  <option value="">Retain Current Custodian</option>
                  {userList.map(u => (
                    <option key={u.id} value={u.id}>{u.name} ({u.department || 'Employee'})</option>
                  ))}
                </select>
              </div>
              <div>
                <SoftwareLicenceMultiSelect
                  licences={licenceList}
                  selectedIds={transferForm.softwareLicenceIds || []}
                  onChange={(ids) => setTransferForm((prev) => ({ ...prev, softwareLicenceIds: ids }))}
                  label="Software & Licences"
                  helperText="(Optional - Transfer or retain licences)"
                  placeholder="Select software licences to transfer..."
                />
              </div>
              <div>
                <label className="block font-semibold mb-1 text-foreground">Transfer Reason & Waybill</label>
                <input
                  type="text"
                  value={transferForm.transferReason}
                  onChange={(e) => setTransferForm({ ...transferForm, transferReason: e.target.value })}
                  className="w-full bg-background border border-border rounded-lg p-2 text-foreground"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="inTransitCheck"
                  checked={transferForm.inTransit}
                  onChange={(e) => setTransferForm({ ...transferForm, inTransit: e.target.checked })}
                  className="rounded border-border text-primary"
                />
                <label htmlFor="inTransitCheck" className="text-foreground font-semibold">Mark as In-Transit</label>
              </div>

              <div className="p-3 bg-muted/40 rounded-lg space-y-1">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="transferEmailCheck"
                    checked={sendEmailNotification}
                    onChange={(e) => setSendEmailNotification(e.target.checked)}
                    className="rounded border-border text-primary"
                  />
                  <label htmlFor="transferEmailCheck" className="text-foreground font-semibold">
                    Send Email Notification
                  </label>
                </div>
                <p className="text-[11px] text-muted-foreground pl-5">
                  Sends transfer notice with waybill and logistics details to the new custodian / branch lead.
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" size="sm" type="button" onClick={() => setShowTransferModal(false)}>Cancel</Button>
                <Button variant="default" size="sm" type="submit" disabled={submitting}>
                  {submitting ? 'Dispatching...' : 'Dispatch Transfer'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Audit Modal (Stage 4) */}
      {showAuditModal && targetAsset && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-card w-full max-w-md rounded-xl border border-border shadow-2xl p-6">
            <h3 className="text-lg font-bold text-foreground mb-1">Stage 4: Physical Audit Verification</h3>
            <p className="text-xs text-muted-foreground mb-4">
              Perform physical verification for {targetAsset.name} ({targetAsset.asset_id}).
            </p>
            <form onSubmit={submitAudit} className="space-y-4 text-xs">
              <div>
                <label className="block font-semibold mb-1 text-foreground">Audit Status Result</label>
                <select
                  value={auditForm.auditResult}
                  onChange={(e) => setAuditForm({ ...auditForm, auditResult: e.target.value })}
                  className="w-full bg-background border border-border rounded-lg p-2 text-foreground"
                >
                  <option value="Verified">Verified (Present & Operational)</option>
                  <option value="Maintenance Required">Maintenance Required</option>
                  <option value="Discrepancy Noted">Discrepancy Noted</option>
                  <option value="Audit Due">Audit Due</option>
                </select>
              </div>
              <div>
                <label className="block font-semibold mb-1 text-foreground">Physical Condition Rating</label>
                <select
                  value={auditForm.condition}
                  onChange={(e) => setAuditForm({ ...auditForm, condition: e.target.value })}
                  className="w-full bg-background border border-border rounded-lg p-2 text-foreground"
                >
                  <option value="Optimal">Optimal (Like New)</option>
                  <option value="Good">Good (Normal wear)</option>
                  <option value="Fair">Fair (Minor cosmetic wear)</option>
                  <option value="Poor">Poor (Requires inspection)</option>
                  <option value="Damaged">Damaged / Non-Functional</option>
                </select>
              </div>
              <div>
                <label className="block font-semibold mb-1 text-foreground">Auditor Name</label>
                {auditorUsers.length > 0 ? (
                  <select
                    value={auditForm.auditorName}
                    onChange={(e) => setAuditForm({ ...auditForm, auditorName: e.target.value })}
                    className="w-full bg-background border border-border rounded-lg p-2 text-foreground"
                    required
                  >
                    <option value="">-- Select Auditor --</option>
                    {auditorUsers.map((auditor) => (
                      <option key={auditor.id} value={auditor.name}>
                        {auditor.name} ({auditor.email || auditor.department || 'Auditor'})
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="space-y-1">
                    <select
                      value={auditForm.auditorName}
                      onChange={(e) => setAuditForm({ ...auditForm, auditorName: e.target.value })}
                      className="w-full bg-background border border-border rounded-lg p-2 text-foreground"
                    >
                      <option value="">-- No users with role 'Auditor' found --</option>
                      {userList.map((u) => (
                        <option key={u.id} value={u.name}>
                          {u.name} ({u.role?.name || u.role || 'User'})
                        </option>
                      ))}
                    </select>
                    <p className="text-[11px] text-amber-500">
                      Only users with the role "Auditor" are shown. Assign the Auditor role in User Management.
                    </p>
                  </div>
                )}
              </div>
              <div>
                <label className="block font-semibold mb-1 text-foreground">Audit Inspection Notes</label>
                <textarea
                  value={auditForm.notes}
                  onChange={(e) => setAuditForm({ ...auditForm, notes: e.target.value })}
                  rows={2}
                  className="w-full bg-background border border-border rounded-lg p-2 text-foreground"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" size="sm" type="button" onClick={() => setShowAuditModal(false)}>Cancel</Button>
                <Button variant="default" size="sm" type="submit" disabled={submitting}>
                  {submitting ? 'Recording...' : 'Record Audit Pass'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Retire Modal (Stage 5) */}
      {showRetireModal && targetAsset && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-card w-full max-w-md rounded-xl border border-border shadow-2xl p-6">
            <h3 className="text-lg font-bold text-foreground mb-1 text-rose-500">Stage 5: Decommission & Retire Asset</h3>
            <p className="text-xs text-muted-foreground mb-4">
              Permanently retire {targetAsset.name} ({targetAsset.asset_id}) with data wipe and certified disposal.
            </p>
            <form onSubmit={submitRetire} className="space-y-4 text-xs">
              <div>
                <label className="block font-semibold mb-1 text-foreground">Retirement Reason</label>
                <select
                  value={retireForm.retirementReason}
                  onChange={(e) => setRetireForm({ ...retireForm, retirementReason: e.target.value })}
                  className="w-full bg-background border border-border rounded-lg p-2 text-foreground"
                >
                  <option value="End of Useful Life (36m Cycle)">End of Useful Life (36m Cycle)</option>
                  <option value="Beyond Economical Repair">Beyond Economical Repair</option>
                  <option value="Hardware Failure">Hardware Failure</option>
                  <option value="Technological Obsolescence">Technological Obsolescence</option>
                </select>
              </div>
              <div>
                <label className="block font-semibold mb-1 text-foreground">Disposal & Recycling Method</label>
                <select
                  value={retireForm.disposalMethod}
                  onChange={(e) => setRetireForm({ ...retireForm, disposalMethod: e.target.value })}
                  className="w-full bg-background border border-border rounded-lg p-2 text-foreground"
                >
                  <option value="Certified E-Waste Recycling">Certified E-Waste Recycling</option>
                  <option value="Salvage / Scrap Auction">Salvage / Scrap Auction</option>
                  <option value="Charity / School Donation">Charity / School Donation</option>
                  <option value="Direct Financial Write-off">Direct Financial Write-off</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="dataWipeCheck"
                  checked={retireForm.dataWipeCertified}
                  onChange={(e) => setRetireForm({ ...retireForm, dataWipeCertified: e.target.checked })}
                  className="rounded border-border text-primary"
                />
                <label htmlFor="dataWipeCheck" className="text-foreground font-semibold">
                  NIST 800-88 / DoD Data Wipe Certified
                </label>
              </div>
              <div>
                <label className="block font-semibold mb-1 text-foreground">Salvage Value ($)</label>
                <input
                  type="number"
                  value={retireForm.salvageValue}
                  onChange={(e) => setRetireForm({ ...retireForm, salvageValue: e.target.value })}
                  className="w-full bg-background border border-border rounded-lg p-2 text-foreground"
                />
              </div>
              <div>
                <label className="block font-semibold mb-1 text-foreground">Authorized By</label>
                <input
                  type="text"
                  value={retireForm.authorizedBy}
                  onChange={(e) => setRetireForm({ ...retireForm, authorizedBy: e.target.value })}
                  className="w-full bg-background border border-border rounded-lg p-2 text-foreground"
                />
              </div>

              <div className="p-3 bg-muted/40 rounded-lg space-y-1">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="retireEmailCheck"
                    checked={sendEmailNotification}
                    onChange={(e) => setSendEmailNotification(e.target.checked)}
                    className="rounded border-border text-primary"
                  />
                  <label htmlFor="retireEmailCheck" className="text-foreground font-semibold">
                    Send Decommissioning Certificate Email
                  </label>
                </div>
                <p className="text-[11px] text-muted-foreground pl-5">
                  Sends permanent retirement certificate and disposal record to IT director and compliance archive.
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" size="sm" type="button" onClick={() => setShowRetireModal(false)}>Cancel</Button>
                <Button variant="destructive" size="sm" type="submit" disabled={submitting}>
                  {submitting ? 'Retiring...' : 'Confirm Retirement'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LifecyclePipelineView;
